import { Checkbox, ListItemText, MenuItem } from '@mui/material';
import { filtersType, selectAllType } from './useFilteredRotationsLocalStorage';

type useDatagridMatricesFilterParams = {
  stateIsOrganic: string;
  selectedFilters: filtersType;
  setSelectedFilters: React.Dispatch<React.SetStateAction<filtersType>>;
  selectAllFilters: selectAllType;
  setSelectAllFilters: React.Dispatch<React.SetStateAction<selectAllType>>;
  setToggleFilter: React.Dispatch<React.SetStateAction<boolean>>;
};

type itemsListType = {
  id: number;
  name: string;
  [key: string]: any;
};

type FilterType = 'rows' | 'columns';

const useDatagridMatricesFilter = ({
  stateIsOrganic,
  selectedFilters,
  setSelectedFilters,
  selectAllFilters,
  setSelectAllFilters,
  setToggleFilter,
}: useDatagridMatricesFilterParams) => {
  const handleToggleFilter = () => {
    setToggleFilter((prev) => !prev);
  };

  const handleOnChangeFilter = (
    itemIds: Array<string | number>,
    type: FilterType,
    itemsList: itemsListType[]
  ) => {
    const updatedFilters = { ...selectedFilters?.[stateIsOrganic]?.[type] };
    const isSelectAll = itemIds.includes('all');
    const allFiltersUnchecked = Object.values(
      selectedFilters?.[stateIsOrganic]?.[type] || {}
    ).every((value) => !value);

    if (itemsList) {
      itemsList?.forEach((item) => {
        if (isSelectAll) {
          const isChecked = !selectAllFilters[stateIsOrganic]?.[type];
          setSelectAllFilters((prev: selectAllType) => ({
            ...prev,
            [stateIsOrganic]: {
              ...prev[stateIsOrganic],
              [type]: isChecked,
            },
          }));
          updatedFilters[item.id] = isChecked;
          if (allFiltersUnchecked) {
            updatedFilters[item.id] = true;
          }
        }
        if (itemIds.includes(item.id)) {
          updatedFilters[item.id] = !selectedFilters?.[stateIsOrganic]?.[type]?.[item.id];
          setSelectAllFilters((prev: selectAllType) => ({
            ...prev,
            [stateIsOrganic]: {
              ...prev[stateIsOrganic],
              [type]: true,
            },
          }));
        } else if (selectedCount(type) > 1) {
          setSelectAllFilters((prev: selectAllType) => ({
            ...prev,
            [stateIsOrganic]: {
              ...prev[stateIsOrganic],
              [type]: true,
            },
          }));
        }
      });

      const allFiltersUncheckedUpdated = Object.values(updatedFilters).every((value) => !value);
      if (allFiltersUncheckedUpdated) {
        setSelectAllFilters((prev: selectAllType) => ({
          ...prev,
          [stateIsOrganic]: {
            ...prev[stateIsOrganic],
            [type]: false,
          },
        }));
      }
      setSelectedFilters((prev: filtersType) => ({
        ...prev,
        [stateIsOrganic]: {
          ...prev[stateIsOrganic],
          [type]: updatedFilters,
        },
      }));
    }
  };

  const renderFilterMenuItems = (
    item: itemsListType,
    selectedFilters: Record<number, boolean> | undefined
  ) => {
    return (
      <MenuItem key={item.id} value={item.id}>
        <Checkbox checked={selectedFilters?.[item.id]} />
        <ListItemText primary={item.name} />
      </MenuItem>
    );
  };

  const selectedCount = (type: FilterType) => {
    return Object.values(selectedFilters?.[stateIsOrganic]?.[type] || {}).filter((value) => value)
      .length;
  };

  const isIndeterminate = (type: FilterType) => {
    const totalCount = Object.keys(selectedFilters?.[stateIsOrganic]?.[type] || {}).length;
    const countSelected = selectedCount(type);
    return countSelected > 0 && countSelected !== totalCount;
  };

  return {
    handleOnChangeFilter,
    renderFilterMenuItems,
    handleToggleFilter,
    isIndeterminate,
  };
};

export default useDatagridMatricesFilter;
